import {
  SCRIBE_SUGGESTION_MARKER_PROPERTY,
  ScribeSuggestionFieldMarkers,
  ScribeSuggestionMarker,
  ScribeSuggestionMarkerTarget,
} from './scribe-suggestion-marker.model';

export function normalizeScribeSuggestionFieldMarkers(
  value: unknown,
): ScribeSuggestionFieldMarkers | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const markers = Object.entries(value as Record<string, unknown>).reduce(
    (result, [fieldKey, isMarked]) => {
      if (fieldKey && isMarked === true) {
        result[fieldKey] = true;
      }

      return result;
    },
    {} as ScribeSuggestionFieldMarkers,
  );

  return Object.keys(markers).length ? markers : null;
}

export function getScribeSuggestionMarker(
  target: ScribeSuggestionMarkerTarget | null | undefined,
): ScribeSuggestionMarker | null {
  const marker = target?.[SCRIBE_SUGGESTION_MARKER_PROPERTY];
  if (marker === true) {
    return true;
  }

  return normalizeScribeSuggestionFieldMarkers(marker);
}

export function isScribeSuggestionMarked(
  target: ScribeSuggestionMarkerTarget | null | undefined,
  fieldKey?: string,
): boolean {
  const marker = getScribeSuggestionMarker(target);
  if (marker === true) {
    return true;
  }

  return fieldKey ? marker?.[fieldKey] === true : marker !== null;
}

export function markScribeSuggestion(
  target: ScribeSuggestionMarkerTarget,
  fieldKey?: string,
): void {
  if (!fieldKey) {
    target[SCRIBE_SUGGESTION_MARKER_PROPERTY] = true;
    return;
  }

  const marker = getScribeSuggestionMarker(target);
  if (marker === true) {
    return;
  }

  target[SCRIBE_SUGGESTION_MARKER_PROPERTY] = {
    ...(marker ?? {}),
    [fieldKey]: true,
  };
}

export function clearScribeSuggestionMarker(
  target: ScribeSuggestionMarkerTarget,
  fieldKey?: string,
): void {
  if (!fieldKey) {
    delete target[SCRIBE_SUGGESTION_MARKER_PROPERTY];
    return;
  }

  const marker = getScribeSuggestionMarker(target);
  if (marker === true) {
    delete target[SCRIBE_SUGGESTION_MARKER_PROPERTY];
    return;
  }

  if (!marker?.[fieldKey]) {
    return;
  }

  const remainingMarkers = { ...marker };
  delete remainingMarkers[fieldKey];

  if (Object.keys(remainingMarkers).length) {
    target[SCRIBE_SUGGESTION_MARKER_PROPERTY] = remainingMarkers;
  } else {
    delete target[SCRIBE_SUGGESTION_MARKER_PROPERTY];
  }
}

export function copyScribeSuggestionMarker(
  source: ScribeSuggestionMarkerTarget | null | undefined,
  target: ScribeSuggestionMarkerTarget,
): void {
  const marker = getScribeSuggestionMarker(source);
  if (marker === null) {
    delete target[SCRIBE_SUGGESTION_MARKER_PROPERTY];
    return;
  }

  target[SCRIBE_SUGGESTION_MARKER_PROPERTY] = marker === true ? true : { ...marker };
}
